import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { TESTIMONIALS as FALLBACK } from "@/lib/content";

/**
 * Auto-rotating testimonials carousel.
 * Fetches from /api/testimonials, falls back to seeded content if API empty.
 */
export default function TestimonialsCarousel({ heading, eyebrow = "Testimonials" }) {
  const [items, setItems] = useState(FALLBACK);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    api
      .get("/testimonials")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setItems(r.data);
      })
      .catch(() => {});
  }, []);

  const total = items.length;

  useEffect(() => {
    if (paused || total < 2) return;
    const id = setInterval(() => setActive((i) => (i + 1) % total), 6000);
    return () => clearInterval(id);
  }, [paused, total]);

  if (total === 0) return null;
  const current = items[Math.min(active, total - 1)];

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
      <p className="eyebrow mb-3">{eyebrow}</p>
      <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
        {heading || (
          <>
            Real women, <span className="italic text-[#E8A6B8]">real results</span>
          </>
        )}
      </h2>

      <div
        className="relative mt-14"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
      >
        <div className="bg-white rounded-[2rem] border border-[#F3C9D3]/40 p-10 sm:p-14 lg:p-16 min-h-[280px] flex flex-col justify-center shadow-[0_20px_60px_rgba(232,166,184,0.12)]">
          <Quote className="text-[#E8A6B8] mx-auto mb-6" size={32} strokeWidth={1.5} />
          <p
            key={current.quote}
            data-testid={`testimonial-${active}`}
            className="font-serif-display italic text-3xl sm:text-4xl text-[#3A3A3A] leading-tight fade-in"
          >
            "{current.quote}"
          </p>
          <p className="mt-6 text-sm sm:text-base text-[#5C5C5C] leading-relaxed max-w-xl mx-auto">
            {current.body}
          </p>
          <p className="mt-7 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#3A3A3A]">
            {current.name}
            {current.role && (
              <>
                <span className="text-[#E8A6B8] mx-2">·</span>
                <span className="text-[#5C5C5C] font-normal normal-case tracking-normal">
                  {current.role}
                </span>
              </>
            )}
          </p>
        </div>

        {total > 1 && (
          <>
            <button
              onClick={() => setActive((i) => (i - 1 + total) % total)}
              className="absolute top-1/2 -translate-y-1/2 -left-4 sm:-left-6 bg-white rounded-full p-3 border border-[#F3C9D3]/40 hover:bg-[#F3C9D3]/40 transition-all"
              data-testid="testimonial-prev"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={18} className="text-[#3A3A3A]" />
            </button>
            <button
              onClick={() => setActive((i) => (i + 1) % total)}
              className="absolute top-1/2 -translate-y-1/2 -right-4 sm:-right-6 bg-white rounded-full p-3 border border-[#F3C9D3]/40 hover:bg-[#F3C9D3]/40 transition-all"
              data-testid="testimonial-next"
              aria-label="Next testimonial"
            >
              <ChevronRight size={18} className="text-[#3A3A3A]" />
            </button>
          </>
        )}
      </div>

      {total > 1 && (
        <div className="flex justify-center gap-2 mt-8">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              data-testid={`testimonial-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === active ? "w-8 bg-[#E8A6B8]" : "w-3 bg-[#F3C9D3]"
              }`}
              aria-label={`Go to testimonial ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
